import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AlertCircle, Save } from 'react-feather';
import config from '../../../../config';
import ModalShell from '../shared/ModalShell';

const empty = { country_name: '', country_code: '', country_currency: '' };

/**
 * Add / Edit modal for the Country entity.
 *
 * `mode === 'add'` posts to /country; `mode === 'edit'` first fetches
 * /country/:id, then posts to /country/updatecountry/:id on submit.
 */
const CountryFormModal = ({ mode = 'add', entityId, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState(empty);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(mode === 'edit');

    const concessionaireId = localStorage.getItem('concessionaireId');
    const loginId = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = { Authorization: `${token}` };

    useEffect(() => {
        if (mode !== 'edit' || !entityId) return;
        let cancelled = false;
        setLoading(true);
        axios
            .get(`${config.hostname}/country/${entityId}`, { headers })
            .then((res) => {
                if (cancelled) return;
                setForm({
                    country_name: res.data.country_name || '',
                    country_code: res.data.country_code || '',
                    country_currency: res.data.country_currency || '',
                });
            })
            .catch((err) => {
                console.error(err);
                toast.error(t('Failed to load country'));
                onClose?.();
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [mode, entityId]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const validate = () => {
        const next = {};
        if (!form.country_name.trim()) next.country_name = t('Country name is required');
        if (!form.country_code.trim()) next.country_code = t('Country code is required');
        if (!form.country_currency.trim()) next.country_currency = t('Currency is required');
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);

        const payload = {
            ...form,
            country_concessionaire_id: concessionaireId,
            login_id: loginId,
        };

        const req = mode === 'add'
            ? axios.post(`${config.hostname}/country`, payload, { headers })
            : axios.post(`${config.hostname}/country/updatecountry/${entityId}`, payload, { headers });

        req
            .then(() => {
                toast.success(mode === 'add' ? t('Country added') : t('Country updated'));
                onSaved?.();
                onClose?.();
            })
            .catch((err) => {
                console.error(err);
                toast.error(t('Save failed'));
            })
            .finally(() => setSaving(false));
    };

    return (
        <ModalShell title={mode === 'add' ? t('Add country') : t('Edit country')} onClose={onClose}>
            {loading ? (
                <div className="v3-sadmin-empty"><p>{t('Loading…')}</p></div>
            ) : (
                <form className="v3-sadmin-form" onSubmit={handleSubmit}>
                    <div className="v3-sadmin-form-grid">
                        <label className="v3-field">
                            <span className="v3-label">{t('Country name')}</span>
                            <input
                                className="v3-input"
                                value={form.country_name}
                                onChange={set('country_name')}
                                placeholder={t('United Arab Emirates')}
                                autoFocus
                            />
                            {errors.country_name && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.country_name}</span>
                            )}
                        </label>
                        <label className="v3-field">
                            <span className="v3-label">{t('Country code')}</span>
                            <input
                                className="v3-input"
                                value={form.country_code}
                                onChange={set('country_code')}
                                placeholder="AE"
                                maxLength={5}
                            />
                            {errors.country_code && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.country_code}</span>
                            )}
                        </label>
                        <label className="v3-field">
                            <span className="v3-label">{t('Currency')}</span>
                            <input
                                className="v3-input"
                                value={form.country_currency}
                                onChange={set('country_currency')}
                                placeholder="AED"
                                maxLength={8}
                            />
                            {errors.country_currency && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.country_currency}</span>
                            )}
                        </label>
                    </div>
                    <div className="v3-sadmin-form-actions">
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={onClose} disabled={saving}>
                            {t('Cancel')}
                        </button>
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                            <Save size={14} /> {saving ? t('Saving…') : mode === 'add' ? t('Add country') : t('Save changes')}
                        </button>
                    </div>
                </form>
            )}
        </ModalShell>
    );
};

export default CountryFormModal;
